import {create} from "zustand";
import {identifyUser} from "../Infra/analytics/posthog.js";
import {clearSentryUser, setSentryUser} from "../Infra/monitoring/sentry.ts";
import {clearSessionAvatar} from "../util/avatarUtils";
import useBetbyStore from "./Betby/useBetbyStore.store";
import {dropStorage, getStorage, setStorage} from "./util.store";
import {decodeMyToken, isTokenExpired, normalizeStoredAccessToken} from "./util.token";

// Store query client reference to clear cache on logout
let queryClient = null;

export const setQueryClient = client => {
  queryClient = client;
};

export const useAuthStore = create((set, get) => ({
  authStatus: "checking",
  token: undefined,
  user: undefined,
  showAuthModal: false,
  authForm: "login",
  handleAuthForm: form => {
    set({authForm: form});
  },
  handleShowAuthModal: () => {
    const showAuthModal = !get().showAuthModal;
    set({showAuthModal});
  },
  validateSession: async () => {
    const accessToken = normalizeStoredAccessToken(getStorage("token"));

    if (!accessToken) {
      set({
        authStatus: "unauthenticated",
        token: undefined,
        user: undefined,
      });
      return;
    }

    const tokenPayload = decodeMyToken(accessToken);
    const isTokenExp = isTokenExpired(tokenPayload);

    if (isTokenExp || !tokenPayload) {
      await get().logout();
      return;
    }

    set({
      authStatus: "authenticated",
      token: accessToken,
      user: tokenPayload,
    });
  },
  login: async ({data}) => {
    const {access_token} = data;
    if (access_token) {
      const tokenPayload = decodeMyToken(access_token);

      // Persist the new token first so any refetch triggered by the cache
      // clear below runs with the new account's credentials, never the
      // previous one's (the request layer reads the token from this cookie).
      setStorage("token", access_token, 0);

      // Account switch (login or auto-login after registration): wipe ALL
      // cached state from any previous session. Previously this only removed
      // three query keys, which leaked the prior account's gamer profile,
      // avatar, wallet, etc. into the new user's UI. Mirror logout()'s full
      // cleanup so no stale account data can ever bleed through.
      if (queryClient) {
        queryClient.clear();
      }
      clearSessionAvatar();

      set({
        authStatus: "authenticated",
        token: access_token,
        user: tokenPayload,
        showAuthModal: false,
      });
      identifyUser(tokenPayload);
      setSentryUser(tokenPayload);

      // Retry FCM token registration if there's a pending token (native app)
      try {
        const {retryTokenRegistration} = await import("../services/nativeBridge.service");
        await retryTokenRegistration();
      } catch (_error) {}

      // Note: Lotoven redirect is now handled by ProtectedHomePage component
    }
  },
  logout: async () => {
    // Check if user came from mobile login
    const isMobileLoginUser = getStorage("isMobileLoginUser") === "true";

    // Note: We keep the FCM token registered even after logout
    // This allows sending notifications about freespins, prizes, promotions
    // even when user is not actively logged in

    dropStorage("modalSessionInfoShown");
    dropStorage("token");
    const _checkExtra = getStorage("token");
    // Clear session avatar when logging out
    clearSessionAvatar();

    // Clear all React Query cache when logging out
    if (queryClient) {
      queryClient.clear();
    }

    // Clear betby token so the home banner reinitializes cleanly
    useBetbyStore.getState().setTokenBetby(null);

    clearSentryUser();

    set({
      authStatus: "unauthenticated",
      token: undefined,
      user: undefined,
    });

    // Redirect mobile users back to mobile login
    if (isMobileLoginUser) {
      // Use a small delay to ensure state is updated
      setTimeout(() => {
        window.location.href = "/mobile-login";
      }, 100);
    }
  },
}));
